import { TimeSlot, CachedSlotData } from '../types/booking';

/**
 * Unified Cache Manager with LRU functionality
 * 
 * This provides a central caching mechanism for the application
 * with memory usage control through LRU eviction policy.
 */

// Types for cache entries
export type CacheEntry<T> = {
  data: T;
  timestamp: number;
  lastAccessed: number;
};

// Cache options interface
export interface CacheOptions {
  maxEntries: number;         // Maximum number of entries to store in cache
  defaultTTL: number;         // Default time-to-live in milliseconds
  staleTTL: number;           // Time after which data is considered stale
}

export class CacheManager<T = any> {
  private cache: Map<string, CacheEntry<T>> = new Map();
  private options: CacheOptions;
  private subscribers: Map<string, Set<() => void>> = new Map();
  
  constructor(options: Partial<CacheOptions> = {}) {
    // Default options
    this.options = {
      maxEntries: 100,              // Default to 100 entries max
      defaultTTL: 5 * 60 * 1000,    // Default TTL: 5 minutes
      staleTTL: 30 * 60 * 1000,     // Stale TTL: 30 minutes
      ...options
    };
  }
  
  /**
   * Set a value in the cache
   */
  set(key: string, value: T): void {
    const now = Date.now();
    
    // LRU eviction if we're at capacity
    if (this.cache.size >= this.options.maxEntries && !this.cache.has(key)) {
      this.evictLRU();
    }
    
    this.cache.set(key, {
      data: value,
      timestamp: now,
      lastAccessed: now
    });
    
    // Notify subscribers
    this.notifySubscribers(key);
  }
  
  /**
   * Get a value from the cache
   * @param allowStale Whether to return stale data if fresh data isn't available
   * @returns The cached value or undefined if not found/expired
   */
  get<K extends T = T>(key: string, allowStale = false): K | undefined {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return undefined;
    }
    
    const now = Date.now();
    const age = now - entry.timestamp;
    
    // Update the last accessed time
    entry.lastAccessed = now;
    
    // Check if the entry is valid
    if (age < this.options.defaultTTL) {
      return entry.data as K;
    }
    
    // Check if we can use stale data
    if (allowStale && age < this.options.staleTTL) {
      return entry.data as K;
    }
    
    return undefined;
  }
  
  /**
   * Check if a key exists in the cache and is not expired
   */
  has(key: string, allowStale = false): boolean {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return false;
    }
    
    const now = Date.now();
    const age = now - entry.timestamp;
    
    if (age < this.options.defaultTTL) {
      return true;
    }
    
    return allowStale && age < this.options.staleTTL;
  }
  
  /**
   * Remove a key from the cache
   */
  delete(key: string): boolean {
    const result = this.cache.delete(key);
    if (result) {
      this.notifySubscribers(key);
    }
    return result;
  }
  
  /**
   * Clear all entries from the cache
   */
  clear(): void {
    this.cache.clear();
    // Notify all subscribers
    for (const key of this.subscribers.keys()) {
      this.notifySubscribers(key);
    }
  }
  
  /**
   * Delete entries matching a prefix
   */
  deleteByPrefix(prefix: string): void {
    const keysToDelete: string[] = [];
    
    // Find all keys with this prefix
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    }
    
    // Delete the keys
    for (const key of keysToDelete) {
      this.cache.delete(key);
      this.notifySubscribers(key);
    }
  }
  
  /**
   * Get the size of the cache
   */
  size(): number {
    return this.cache.size;
  }
  
  /**
   * Evict the least recently used entry
   * @private
   */
  private evictLRU(): void {
    let oldestKey: string | null = null;
    let oldestAccessed = Date.now();
    
    // Find the least recently used entry
    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccessed < oldestAccessed) {
        oldestAccessed = entry.lastAccessed;
        oldestKey = key;
      }
    }
    
    // Delete the oldest entry
    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.notifySubscribers(oldestKey);
    }
  }
  
  /**
   * Subscribe to changes for a specific key or key pattern
   */
  subscribe(keyPattern: string, callback: () => void): () => void {
    if (!this.subscribers.has(keyPattern)) {
      this.subscribers.set(keyPattern, new Set());
    }
    
    this.subscribers.get(keyPattern)!.add(callback);
    
    // Return unsubscribe function
    return () => {
      const subscribers = this.subscribers.get(keyPattern);
      if (subscribers) {
        subscribers.delete(callback);
        if (subscribers.size === 0) {
          this.subscribers.delete(keyPattern);
        }
      }
    };
  }
  
  /**
   * Notify subscribers of a change
   * @private
   */
  private notifySubscribers(key: string): void {
    // Notify exact key subscribers
    const exactSubscribers = this.subscribers.get(key);
    if (exactSubscribers) {
      for (const callback of exactSubscribers) {
        callback();
      }
    }
    
    // Notify pattern subscribers
    for (const [pattern, callbacks] of this.subscribers.entries()) {
      if (pattern !== key && key.includes(pattern)) {
        for (const callback of callbacks) {
          callback();
        }
      }
    }
  }
}

// Create specialized cache instances
export const slotCache = new CacheManager<CachedSlotData>({
  maxEntries: 50,  // Store up to 50 months of data
  defaultTTL: 5 * 60 * 1000,  // 5 minutes
  staleTTL: 30 * 60 * 1000,   // 30 minutes
});

export const apiResponseCache = new CacheManager<TimeSlot[]>({
  maxEntries: 100, // Store up to 100 API responses
  defaultTTL: 5 * 60 * 1000,  // 5 minutes
  staleTTL: 30 * 60 * 1000,   // 30 minutes
});

// Helper functions for slot cache
export const getMonthKey = (meetingTypeId: string | number, date: Date, timezone?: string): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return `${meetingTypeId}-${year}-${month}${timezone ? `-${timezone}` : ''}`;
};

export const getDateKey = (date: Date, timezone?: string): string => {
  if (timezone) {
    // FIXED: Use manual date component extraction to match BookingDataProvider
    // This ensures calendar highlighting and slot retrieval use the same cache keys
    const { utcToZonedTime } = require('date-fns-tz');
    const dateInTimezone = utcToZonedTime(date, timezone);
    const year = dateInTimezone.getFullYear();
    const month = String(dateInTimezone.getMonth() + 1).padStart(2, '0');
    const day = String(dateInTimezone.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } else {
    // Fallback to simple format when no timezone specified
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
};

// Default export for convenient importing
const cacheManager = {
  slotCache,
  apiResponseCache,
  getMonthKey,
  getDateKey
};

export default cacheManager; 